import { createBrownNoiseBuffer, createReverbImpulse, createWhiteNoiseBuffer } from './noise';

/**
 * Klangschicht.
 *
 * Vollstaendig synthetisiert, ohne eine einzige Audiodatei. Das passt zum
 * Rest des Projekts — alles kommt aus Regeln statt aus Assets —, spart die
 * Lizenzfrage und kostet keine Ladezeit. Der Preis ist, dass es kein Orchester
 * gibt: Was hier moeglich ist, sind Flaechen, einzelne Toene und Signale. Fuer
 * ein Spiel, das von Stille und Anspannung lebt, ist das kein Verlust.
 *
 * Browser starten Audio erst nach einer Nutzerhandlung. Deshalb `resume()`,
 * das beim ersten Klick gerufen wird.
 */

const NOTE_SEMITONES: Readonly<Record<string, number>> = {
  C: 0, 'C#': 1, D: 2, Eb: 3, E: 4, F: 5, 'F#': 6, G: 7, Ab: 8, A: 9, Bb: 10, B: 11,
};

/** Wandelt `"Eb3"` in eine Frequenz in Hertz. */
export function noteToHz(note: string): number {
  const match = /^([A-G][b#]?)(-?\d+)$/.exec(note);
  if (match === null) return 440;

  const semitone = NOTE_SEMITONES[match[1] as string] ?? 0;
  const octave = Number(match[2]);
  // A4 = 440 Hz liegt bei Halbton 9 in Oktave 4.
  return 440 * 2 ** ((octave - 4) + (semitone - 9) / 12);
}

export type CueKind = 'select' | 'confirm' | 'sonar' | 'alert' | 'discovery' | 'creak';

/** Schallgeschwindigkeit in Meerwasser, in Metern je Sekunde. */
const SPEED_OF_SOUND_IN_WATER = 1500;

/**
 * Weiter entfernte Ziele liefern kein brauchbares Echo mehr: Nach etwa
 * zwanzig Sekunden ordnet niemand einen Rueckwurf mehr dem Ping zu, der ihn
 * ausgeloest hat.
 */
const SONAR_ECHO_RANGE_NM = 8;

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private droneGain: GainNode | null = null;
  private droneVoices: OscillatorNode[] = [];
  private droneFilter: BiquadFilterNode | null = null;
  private muted = false;

  /** Hallweg — er verortet alles in einem grossen, nassen Raum. */
  private reverb: ConvolverNode | null = null;
  private wet: GainNode | null = null;

  /** Meeresschicht: braunes Rauschen als Wasser, weisses als Gischt. */
  private seaSource: AudioBufferSourceNode | null = null;
  private seaFilter: BiquadFilterNode | null = null;
  private seaGain: GainNode | null = null;
  private sprayGain: GainNode | null = null;

  private brownNoise: AudioBuffer | null = null;
  private whiteNoise: AudioBuffer | null = null;

  /** Laufender Sonarzyklus. */
  private sonarTimer: number | null = null;
  /** Getaucht klingt alles gedaempfter. */
  private submerged = false;

  /** Knallkrebse des Flachwassers. */
  private shrimpTimer: number | null = null;
  private shrimpActive = false;

  /**
   * Wird nach jedem Ping gerufen. Die Szene entscheidet daraufhin, ob ein
   * Echo zurueckkommt — der Motor kennt die Spielwelt nicht.
   */
  onPing: (() => void) | null = null;

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Startet oder weckt den Audiokontext. Muss aus einer Nutzerhandlung heraus
   * gerufen werden, sonst bleibt er in `suspended` stehen.
   */
  resume(): void {
    if (this.ctx === null) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor === undefined) return;

      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 0.5;
      this.master.connect(this.ctx.destination);

      // Hallweg parallel zum Direktsignal. Alles Klingende geht in beide.
      this.reverb = this.ctx.createConvolver();
      this.reverb.buffer = createReverbImpulse(this.ctx, 4.5, 2.4);
      this.wet = this.ctx.createGain();
      this.wet.gain.value = 0.55;
      this.wet.connect(this.reverb);
      this.reverb.connect(this.master);

      this.brownNoise = createBrownNoiseBuffer(this.ctx);
      this.whiteNoise = createWhiteNoiseBuffer(this.ctx);
    }

    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master !== null && this.ctx !== null) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.5, this.ctx.currentTime, 0.1);
    }
  }

  /**
   * Die Grundflaeche: zwei leicht verstimmte Oszillatoren auf dem Grundton
   * plus die Quinte, durch einen tiefen Filter.
   *
   * Die Verstimmung erzeugt eine langsame Schwebung — der Klang steht nie
   * ganz still, ohne dass sich etwas bewegt. Genau das macht ihn unruhig.
   */
  startDrone(rootNote: string): void {
    this.resume();
    if (this.ctx === null || this.master === null) return;

    this.stopDrone();

    const root = noteToHz(rootNote);
    this.droneGain = this.ctx.createGain();
    this.droneGain.gain.value = 0;

    this.droneFilter = this.ctx.createBiquadFilter();
    this.droneFilter.type = 'lowpass';
    this.droneFilter.frequency.value = 320;
    this.droneFilter.Q.value = 0.7;

    this.droneGain.connect(this.droneFilter);
    this.droneFilter.connect(this.master);

    // Grundton doppelt (leicht verstimmt) und die Quinte darueber.
    for (const [frequency, detune] of [
      [root, -4],
      [root, 5],
      [root * 1.5, 2],
    ] as const) {
      const osc = this.ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = frequency;
      osc.detune.value = detune;
      osc.connect(this.droneGain);
      osc.start();
      this.droneVoices.push(osc);
    }

    // Langsam einblenden, damit der Einsatz nicht als Knacken hoerbar wird.
    this.droneGain.gain.setTargetAtTime(0.16, this.ctx.currentTime, 2.5);
  }

  stopDrone(): void {
    if (this.ctx === null) return;

    const gain = this.droneGain;
    const voices = this.droneVoices;
    this.droneGain = null;
    this.droneVoices = [];

    if (gain !== null) {
      gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.6);
    }
    // Erst nach dem Ausblenden anhalten, sonst knackt es.
    window.setTimeout(() => {
      for (const osc of voices) {
        try {
          osc.stop();
        } catch {
          // Bereits gestoppt — unkritisch.
        }
      }
    }, 2500);
  }

  /**
   * Spannung der Grundflaeche. Steigt der Seegang oder faellt der Grund weg,
   * oeffnet der Filter und der Klang wird praesenter.
   */
  setTension(amount: number): void {
    if (this.ctx === null) return;
    const clamped = Math.max(0, Math.min(1, amount));

    if (this.droneFilter !== null) {
      this.droneFilter.frequency.setTargetAtTime(240 + clamped * 700, this.ctx.currentTime, 1.5);
    }
    // Schwere See ist lauter und rauer als ruhige.
    if (this.seaGain !== null) {
      this.seaGain.gain.setTargetAtTime(0.07 + clamped * 0.13, this.ctx.currentTime, 2);
    }
    if (this.sprayGain !== null && !this.submerged) {
      this.sprayGain.gain.setTargetAtTime(0.004 + clamped * 0.02, this.ctx.currentTime, 2);
    }
  }

  /** Einzelner Ton mit weichem Ein- und Ausschwingen. */
  playNote(note: string, durationSeconds: number, delaySeconds = 0, volume = 0.12): void {
    if (this.ctx === null || this.master === null) return;

    const start = this.ctx.currentTime + delaySeconds;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc.type = 'triangle';
    osc.frequency.value = noteToHz(note);

    filter.type = 'lowpass';
    filter.frequency.value = 1400;

    gain.gain.setValueAtTime(0, start);
    // Langsamer Anschlag: Es soll nach Glocke unter Wasser klingen, nicht nach Klavier.
    gain.gain.linearRampToValueAtTime(volume, start + Math.min(0.4, durationSeconds * 0.3));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + durationSeconds);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    if (this.wet !== null) gain.connect(this.wet);

    osc.start(start);
    osc.stop(start + durationSeconds + 0.1);
  }

  /** Spielt eine Motivfolge aus `[Note, Dauer]`-Paaren. */
  playMotif(motif: readonly (readonly [string, number])[], volume = 0.1): void {
    let offset = 0;
    for (const [note, duration] of motif) {
      this.playNote(note, duration * 0.9, offset, volume);
      offset += duration;
    }
  }

  /** Kurze Signale fuer Bedienung und Ereignisse. */
  cue(kind: CueKind): void {
    if (this.ctx === null || this.master === null) return;
    const now = this.ctx.currentTime;

    switch (kind) {
      case 'select':
        this.playNote('D5', 0.12, 0, 0.05);
        break;

      case 'confirm':
        this.playNote('D4', 0.3, 0, 0.08);
        this.playNote('A4', 0.4, 0.06, 0.06);
        break;

      case 'sonar':
        this.sonarPing(now);
        break;

      case 'alert':
        // Zwei fallende Toene — ein Notruf klingt nirgends freundlich.
        this.playNote('Bb4', 0.35, 0, 0.09);
        this.playNote('Eb4', 0.6, 0.3, 0.09);
        break;

      case 'creak':
        this.hullCreak(now);
        break;

      case 'discovery':
        this.playNote('D4', 1.2, 0, 0.07);
        this.playNote('F4', 1.4, 0.15, 0.05);
        this.playNote('Bb4', 1.8, 0.3, 0.04);
        break;
    }
  }

  /**
   * Rumpfknarren: tiefes Rauschen durch einen wandernden Bandpass.
   * Der wandernde Filter macht daraus ein Aechzen statt eines Rauschstosses.
   */
  private hullCreak(now: number): void {
    if (this.ctx === null || this.master === null || this.brownNoise === null) return;

    const source = this.ctx.createBufferSource();
    source.buffer = this.brownNoise;
    source.loop = true;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 9;
    filter.frequency.setValueAtTime(90, now);
    filter.frequency.linearRampToValueAtTime(210, now + 1.1);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.3);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    if (this.wet !== null) gain.connect(this.wet);

    source.start(now);
    source.stop(now + 1.4);
  }

  /**
   * Der Sonarping.
   *
   * Kein abfallender Sinus, wie ihn Filme benutzen: Eine klassische Anlage
   * sendet einen kurzen, kraeftigen Dauerton (CW-Puls) von etwa 200 ms. Der
   * Wandler ist ein mechanisches Bauteil — der Einsatz ist hart und perkussiv,
   * nicht angeblasen. Erst am Ende faellt die Tonhoehe leicht ab.
   *
   * Ein Oberton macht ihn metallisch statt fluetend; ohne ihn klingt der Puls
   * nach Testton statt nach Geraet.
   */
  private sonarPing(now: number): void {
    if (this.ctx === null || this.master === null) return;

    const carrier = 1180;
    const pulse = 0.2;

    for (const [ratio, level] of [
      [1, 0.16],
      [2.02, 0.05],
    ] as const) {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(carrier * ratio, now);
      // Nur ein kurzer Abfall zum Schluss — der Puls ist im Kern ein Dauerton.
      osc.frequency.setValueAtTime(carrier * ratio, now + pulse * 0.75);
      osc.frequency.exponentialRampToValueAtTime(carrier * ratio * 0.82, now + pulse);

      // Harter Einsatz, kurzes Halten, schnelles Abfallen.
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(level, now + 0.004);
      gain.gain.setValueAtTime(level, now + pulse * 0.7);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + pulse + 0.12);

      osc.connect(gain);
      gain.connect(this.master);
      if (this.wet !== null) gain.connect(this.wet);

      osc.start(now);
      osc.stop(now + pulse + 0.2);
    }

    this.reverberationWash(now + pulse * 0.6, carrier);
  }

  /**
   * Die Nachhall-Wolke.
   *
   * Das eigentlich Charakteristische am Sonar — und das, was fast alle
   * Nachbauten weglassen. Der Puls wird an Oberflaeche, Grund und allem im
   * Wasser gestreut und kehrt als diffuses, breitbandiges Rauschen zurueck,
   * das ueber Sekunden abklingt. Wer nur den Ping baut, baut das Unwichtigere.
   *
   * Umgesetzt als Rauschen durch einen Bandpass um die Sendefrequenz, dessen
   * Guete ueber die Zeit aufgeht: Die Rueckstreuung wird breiter und dumpfer,
   * je laenger sie unterwegs war.
   */
  private reverberationWash(now: number, carrier: number): void {
    if (this.ctx === null || this.master === null || this.whiteNoise === null) return;

    const source = this.ctx.createBufferSource();
    source.buffer = this.whiteNoise;
    source.loop = true;

    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.setValueAtTime(carrier, now);
    band.frequency.exponentialRampToValueAtTime(carrier * 0.45, now + 3.2);
    band.Q.setValueAtTime(7, now);
    band.Q.linearRampToValueAtTime(1.2, now + 3.2);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.075, now + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 3.4);

    source.connect(band);
    band.connect(gain);
    gain.connect(this.master);
    if (this.wet !== null) gain.connect(this.wet);

    source.start(now);
    source.stop(now + 3.5);
  }

  /**
   * Ein Echo von einem Ziel in `rangeNm` Seemeilen.
   *
   * Die Verzoegerung ist echt gerechnet: Schall laeuft im Meerwasser mit rund
   * 1.500 m/s, die Laufzeit ist also 2 mal Entfernung durch Schallgeschwindigkeit.
   * Auf einer Seemeile sind das 2,5 Sekunden. Weiter entfernte Ziele lohnen
   * sich nicht mehr — nach zwanzig Sekunden ordnet niemand ein Echo mehr dem
   * Ping zu, der es ausgeloest hat.
   *
   * `closing` verschiebt die Tonhoehe: Ein Ziel, auf das man zulaeuft, kommt
   * hoeher zurueck als es gesendet wurde.
   */
  sonarEcho(rangeNm: number, closing = true): void {
    if (this.ctx === null || this.master === null) return;
    if (rangeNm > SONAR_ECHO_RANGE_NM || rangeNm <= 0) return;

    const metres = rangeNm * 1852;
    const delay = (2 * metres) / SPEED_OF_SOUND_IN_WATER;
    const now = this.ctx.currentTime + delay;

    // Naeher heisst lauter — die Rueckstreuung faellt mit der Entfernung.
    const level = 0.05 * (1 - rangeNm / SONAR_ECHO_RANGE_NM) + 0.012;
    const doppler = closing ? 1.03 : 0.97;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = 1180 * doppler;

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(level, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

    osc.connect(gain);
    gain.connect(this.master);
    if (this.wet !== null) gain.connect(this.wet);

    osc.start(now);
    osc.stop(now + 0.4);
  }

  /**
   * Knallkrebse.
   *
   * Tropisches Flachwasser ist voller davon — ein dichtes, unregelmaessiges
   * Knistern, laut genug, dass es Sonar tatsaechlich stoert. Ueber der
   * Bahama-Bank gehoert es zwingend dazu; in der Tiefsee gibt es das nicht.
   * Damit klingen flaches und tiefes Wasser hoerbar verschieden, ohne dass
   * irgendwo eine Anzeige noetig waere.
   */
  private snapAt(time: number): void {
    if (this.ctx === null || this.master === null || this.whiteNoise === null) return;

    const source = this.ctx.createBufferSource();
    source.buffer = this.whiteNoise;
    // Zufaelliger Ausschnitt, damit nicht jeder Knall gleich klingt.
    const offset = Math.random() * 1.5;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 2200 + Math.random() * 3000;

    const gain = this.ctx.createGain();
    const level = 0.008 + Math.random() * 0.02;
    gain.gain.setValueAtTime(level, time);
    // Sehr kurz — ein Knall, kein Zischen.
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.02);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);

    source.start(time, offset, 0.05);
  }

  /** Startet oder beendet das Knistern des Flachwassers. */
  setShallowWater(active: boolean): void {
    if (active === this.shrimpActive) return;
    this.shrimpActive = active;

    if (!active) {
      if (this.shrimpTimer !== null) {
        window.clearInterval(this.shrimpTimer);
        this.shrimpTimer = null;
      }
      return;
    }

    this.resume();
    if (this.ctx === null) return;

    // In Schueben planen statt je Knall einen Zeitgeber zu setzen: Das haelt
    // die Zahl der Timer klein und klingt trotzdem unregelmaessig.
    this.shrimpTimer = window.setInterval(() => {
      if (this.ctx === null) return;
      const base = this.ctx.currentTime;
      const count = 6 + Math.floor(Math.random() * 8);
      for (let i = 0; i < count; i++) {
        this.snapAt(base + Math.random() * 0.5);
      }
    }, 500);
  }

  /**
   * Ein entfernter Ruf aus der Tiefe.
   *
   * Sehr tief, sehr lang, ohne klaren Anfang. In der Wirklichkeit waere das
   * ein Wal oder eine Eisscholle; hier bleibt es bewusst offen — in einem
   * Spiel, in dem ein Seeungeheuer eine der moeglichen Aufloesungen ist, soll
   * niemand sicher sein koennen.
   */
  biologic(): void {
    if (this.ctx === null || this.master === null) return;
    const now = this.ctx.currentTime;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();

    osc.type = 'sine';
    const start = 58 + Math.random() * 26;
    osc.frequency.setValueAtTime(start, now);
    // Ein langsames Gleiten nach unten, wie ein sehr grosser Atemzug.
    osc.frequency.linearRampToValueAtTime(start * 0.62, now + 4.5);

    filter.type = 'lowpass';
    filter.frequency.value = 320;

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.09, now + 1.4);
    gain.gain.linearRampToValueAtTime(0, now + 5);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    if (this.wet !== null) gain.connect(this.wet);

    osc.start(now);
    osc.stop(now + 5.2);
  }

  /**
   * Startet die Meeresschicht.
   *
   * Zwei Lagen: braunes Rauschen durch einen tiefen Filter als Wassermasse,
   * darueber sehr leise weisses Rauschen als Gischt. Beide werden langsam in
   * der Lautstaerke moduliert — das ergibt das An- und Abschwellen, das eine
   * See ausmacht, ohne dass eine einzige Welle berechnet wird.
   */
  startSea(): void {
    this.resume();
    if (this.ctx === null || this.master === null) return;
    if (this.seaSource !== null) return;
    if (this.brownNoise === null || this.whiteNoise === null) return;

    this.seaGain = this.ctx.createGain();
    this.seaGain.gain.value = 0;

    this.seaFilter = this.ctx.createBiquadFilter();
    this.seaFilter.type = 'lowpass';
    this.seaFilter.frequency.value = 520;
    this.seaFilter.Q.value = 0.6;

    this.seaSource = this.ctx.createBufferSource();
    this.seaSource.buffer = this.brownNoise;
    this.seaSource.loop = true;
    this.seaSource.connect(this.seaFilter);
    this.seaFilter.connect(this.seaGain);
    this.seaGain.connect(this.master);
    this.seaSource.start();

    // Gischt: weisses Rauschen, hochpassgefiltert, sehr zurueckhaltend.
    const spray = this.ctx.createBufferSource();
    spray.buffer = this.whiteNoise;
    spray.loop = true;
    const sprayFilter = this.ctx.createBiquadFilter();
    sprayFilter.type = 'highpass';
    sprayFilter.frequency.value = 2600;
    this.sprayGain = this.ctx.createGain();
    this.sprayGain.gain.value = 0;
    spray.connect(sprayFilter);
    sprayFilter.connect(this.sprayGain);
    this.sprayGain.connect(this.master);
    spray.start();

    // Langsame Modulation — der Atem der See.
    const swell = this.ctx.createOscillator();
    swell.frequency.value = 0.07;
    const swellDepth = this.ctx.createGain();
    swellDepth.gain.value = 0.045;
    swell.connect(swellDepth);
    swellDepth.connect(this.seaGain.gain);
    swell.start();

    this.seaGain.gain.setTargetAtTime(0.1, this.ctx.currentTime, 3);
  }

  stopSea(): void {
    const source = this.seaSource;
    this.seaSource = null;
    if (this.ctx !== null && this.seaGain !== null) {
      this.seaGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.8);
      if (this.sprayGain !== null) {
        this.sprayGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.8);
      }
    }
    window.setTimeout(() => {
      try {
        source?.stop();
      } catch {
        // Bereits gestoppt.
      }
    }, 3000);
  }

  /**
   * Der wiederkehrende Sonarzyklus.
   *
   * Ein Ping alle paar Sekunden ist das Geraeusch, das eine Suche auf See
   * ausmacht — regelmaessig, geduldig, und mit einer Pause, in der man auf
   * eine Antwort wartet, die meistens ausbleibt.
   */
  startSonarSweep(intervalSeconds = 9): void {
    this.stopSonarSweep();
    const ping = (): void => {
      this.cue('sonar');
      this.onPing?.();
    };

    this.sonarTimer = window.setInterval(ping, intervalSeconds * 1000);
    // Der erste Ping soll nicht erst nach der vollen Wartezeit kommen.
    window.setTimeout(ping, 1200);
  }

  stopSonarSweep(): void {
    this.onPing = null;
    if (this.sonarTimer !== null) {
      window.clearInterval(this.sonarTimer);
      this.sonarTimer = null;
    }
  }

  /**
   * Getaucht wird alles dumpfer: Wasser schluckt Hoehen, und die Gischt an der
   * Oberflaeche ist von unten nicht mehr zu hoeren.
   */
  setSubmerged(submerged: boolean): void {
    this.submerged = submerged;
    if (this.ctx === null) return;

    if (this.seaFilter !== null) {
      this.seaFilter.frequency.setTargetAtTime(submerged ? 180 : 520, this.ctx.currentTime, 2);
    }
    if (this.sprayGain !== null) {
      this.sprayGain.gain.setTargetAtTime(submerged ? 0 : 0.012, this.ctx.currentTime, 2);
    }
  }

  get isSubmerged(): boolean {
    return this.submerged;
  }
}

/** Eine Instanz fuer das ganze Spiel — Audio ist global. */
export const audio = new AudioEngine();
